import { createContext, useContext, type ReactNode } from 'react';
import type { Lang } from '../lib/countries';
import { en, type Dict } from './en';
import { es } from './es';
import { fr } from './fr';
import { de } from './de';
import { pt } from './pt';
import { it } from './it';
import { tr } from './tr';
import contentRaw from './content.json';

const DICTS: Record<Lang, Dict> = { en, es, fr, de, pt, it, tr };

export const SUPPORTED_LANGS: Lang[] = ['en', 'es', 'fr', 'de', 'pt', 'it', 'tr'];

export function getDict(lang: Lang): Dict {
  return DICTS[lang] ?? en;
}

// 长文本 + SEO meta 的单一真相源(供 React 内容区、applySeo、prerender 共用)
export interface ModeMeta {
  h1: string;
  title: string;
  desc: string;
}
export interface SeoContentBlock {
  aboutTitle: string;
  about: string;
  howToTitle: string;
  howTo: string[];
  modesTitle: string;
  modesIntro: string;
  modes: Record<string, string>;
  faqTitle: string;
  faq: { q: string; a: string }[];
  /** 子模式页用「攻略」替掉「Game Modes」—— 顶部已有模式导航,再列一遍是重复内容 */
  tipsTitle: string;
  // 各子模式专属 SEO(标题/正文/玩法/攻略/FAQ 都聚焦该模式核心关键词)
  seo?: Record<string, { aboutTitle: string; about: string; howTo: string[]; tips?: string[]; faq: { q: string; a: string }[] }>;
}
export interface LangContent {
  meta: Record<string, ModeMeta>;
  content: SeoContentBlock;
  unlimited: { titleSuffix: string; desc: string };
}

const CONTENT = contentRaw as unknown as Record<Lang, LangContent>;
export function getContent(lang: Lang): LangContent {
  return CONTENT[lang] ?? CONTENT.en;
}

interface I18nValue {
  lang: Lang;
  t: Dict;
}

const I18nContext = createContext<I18nValue>({ lang: 'en', t: en });

export function I18nProvider({ lang, children }: { lang: Lang; children: ReactNode }) {
  return <I18nContext.Provider value={{ lang, t: getDict(lang) }}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}
